import { Button, Card, message, Space, Tag, Typography } from 'antd';
import { CheckCircleOutlined, CloseCircleOutlined, CopyOutlined } from '@ant-design/icons';
import { LinkStatus } from '@common';
import ApiKeyManagement from './ApiKeyManagement';
import ApiUsageCard from './ApiUsageCard';
import useAppname from './useAppname';

const { Title, Text } = Typography;

interface Props {
    linkStatus?: number;
}

export default function ApiAppContent({ linkStatus }: Props) {
    const { appName, maindomain } = useAppname();
    const baseUrl = `https://${appName}-proxy.${maindomain}`;
    const isOnline = linkStatus === LinkStatus.ONLINE;

    const copyToClipboard = (text: string) => {
        navigator.clipboard.writeText(text);
        message.success('Copied to clipboard');
    };

    const curlExample = `curl -X GET ${baseUrl}/health \\
  -H "X-ModelPark-Key: YOUR_API_KEY"`;

    const pythonExample = `import requests

response = requests.get(
    "${baseUrl}/health",
    headers={"X-ModelPark-Key": "YOUR_API_KEY"}
)
print(response.json())`;

    return (
        <Space direction="vertical" style={{ width: '100%', padding: '24px', maxWidth: 800 }} size="large">
            <Card>
                <Space direction="vertical" style={{ width: '100%' }} size="small">
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Title level={4} style={{ margin: 0 }}>REST API</Title>
                        <Tag
                            icon={isOnline ? <CheckCircleOutlined /> : <CloseCircleOutlined />}
                            color={isOnline ? 'success' : 'default'}
                        >
                            {isOnline ? 'Online' : 'Offline'}
                        </Tag>
                    </div>
                    <Space>
                        <Text type="secondary">Base URL:</Text>
                        <Text code copyable>{baseUrl}</Text>
                    </Space>
                </Space>
            </Card>

            <ApiKeyManagement appName={appName} />

            <ApiUsageCard appName={appName} />

            <Card title="Quick Start">
                <Space direction="vertical" style={{ width: '100%' }} size="middle">
                    <div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                            <Text strong>cURL</Text>
                            <Button size="small" icon={<CopyOutlined />} onClick={() => copyToClipboard(curlExample)}>
                                Copy
                            </Button>
                        </div>
                        <pre style={{
                            background: '#f5f5f5',
                            padding: 12,
                            borderRadius: 6,
                            fontSize: 13,
                            overflow: 'auto',
                            margin: 0,
                        }}>
                            {curlExample}
                        </pre>
                    </div>
                    <div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                            <Text strong>Python</Text>
                            <Button size="small" icon={<CopyOutlined />} onClick={() => copyToClipboard(pythonExample)}>
                                Copy
                            </Button>
                        </div>
                        <pre style={{
                            background: '#f5f5f5',
                            padding: 12,
                            borderRadius: 6,
                            fontSize: 13,
                            overflow: 'auto',
                            margin: 0,
                        }}>
                            {pythonExample}
                        </pre>
                    </div>
                </Space>
            </Card>
        </Space>
    );
}
